/**
 * In một tài liệu HTML.
 *
 * VÌ SAO IFRAME CHỨ KHÔNG PHẢI window.open: mọi bản in trước đây mở một cửa sổ
 * mới rồi `if (!w) return;` — trình duyệt chặn popup là nút In không làm gì cả
 * và KHÔNG BÁO GÌ. Người dùng bấm, không thấy gì, kết luận chức năng hỏng.
 * Chrome chặn popup theo mặc định khi lần mở không xuất phát trực tiếp từ một
 * cú bấm, nên đây không phải trường hợp hiếm.
 *
 * Iframe cùng trang thì không có gì để chặn: không cửa sổ mới, không cần cấp
 * quyền, và chạy như nhau trên mọi trình duyệt.
 *
 * Tiện thể sửa luôn một chỗ lệch: trước đây có HAI cách tự bật hộp thoại in.
 * Năm bản in nhúng `<script>window.onload=...print()</script>` vào chính tài
 * liệu — nó chạy ở `onload`, tức là TRƯỚC khi ảnh và font tải xong, nên chứng
 * từ hay in ra thiếu ảnh sản phẩm hoặc lệch cột số tiền vì font dự phòng. Năm
 * bản còn lại gọi `w.print()` sau một khoảng chờ đoán mò 250/800ms. Giờ tất cả
 * đi chung một đường và CHỜ ĐÚNG ĐIỀU KIỆN thay vì chờ theo đồng hồ.
 */

/** Chờ ảnh tải xong. Ảnh chưa tải mà in là ra chứng từ thiếu ảnh sản phẩm. */
function imagesReady(doc: Document): Promise<void> {
  const imgs = Array.from(doc.images);
  if (imgs.length === 0) return Promise.resolve();
  return Promise.all(
    imgs.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete) return resolve();
          img.addEventListener("load", () => resolve(), { once: true });
          // Ảnh hỏng KHÔNG được treo bản in: vẫn in, chỉ thiếu đúng ảnh đó.
          img.addEventListener("error", () => resolve(), { once: true });
        }),
    ),
  ).then(() => undefined);
}

/** Chờ font. Thiếu bước này, số tiền in ra bằng font dự phòng, lệch cột. */
function fontsReady(doc: Document): Promise<void> {
  const fonts = (doc as Document & { fonts?: FontFaceSet }).fonts;
  return fonts ? fonts.ready.then(() => undefined) : Promise.resolve();
}

export function printHtml(html: string): void {
  const frame = document.createElement("iframe");
  // Ẩn nhưng vẫn phải có kích thước thật: iframe 0×0 khiến một số trình duyệt
  // dựng trang sai khổ rồi in ra bản bị cắt.
  frame.setAttribute("aria-hidden", "true");
  frame.style.cssText = "position:fixed;right:0;bottom:0;width:210mm;height:297mm;border:0;visibility:hidden;";
  document.body.appendChild(frame);

  const cleanup = () => {
    // Gỡ ở nhịp sau: gỡ ngay trong lúc hộp thoại in còn mở sẽ huỷ luôn bản in.
    setTimeout(() => frame.remove(), 1000);
  };

  // document.write có thể phát onload VÀ đã ở trạng thái complete ngay, nên
  // không có chốt này thì hộp thoại in bật LÊN HAI LẦN.
  let started = false;
  const start = () => {
    if (started) return;
    started = true;
    const w = frame.contentWindow;
    if (!w) return cleanup();
    void Promise.all([imagesReady(w.document), fontsReady(w.document)])
      .then(() => {
        w.addEventListener("afterprint", cleanup, { once: true });
        w.focus();
        w.print();
        // Safari không phát afterprint trong iframe — có lưới an toàn.
        setTimeout(cleanup, 60_000);
      })
      .catch(cleanup);
  };
  frame.onload = start;

  const doc = frame.contentWindow?.document;
  if (!doc) return cleanup();
  doc.open();
  doc.write(html);
  doc.close();
  // document.write không phải lúc nào cũng phát onload; gọi tay nếu tài liệu đã
  // sẵn sàng — `started` lo phần không bị gọi hai lần.
  if (doc.readyState === "complete") start();
}
